import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  alpha,
  useTheme,
  keyframes,
} from "@mui/material";
import {
  VerifiedUser as VerifiedUserIcon,
  ArrowBack as ArrowBackIcon,
  CameraAlt as CameraIcon,
  Badge as BadgeIcon,
  Security as SecurityIcon,
  ErrorOutline as ErrorOutlineIcon,
  School as SchoolIcon,
  OpenInNew as OpenInNewIcon,
  HourglassTop as HourglassTopIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  useCreateIdentitySessionMutation,
  useGetVerificationStatusQuery,
} from "components/Slices/usersApiSlice";

// Initialize Stripe
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "",
);

// Pulse animation for the waiting icon
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.08); opacity: 0.85; }
  100% { transform: scale(1); opacity: 1; }
`;

interface VerificationStepProps {
  onNext: () => void;
  onBack: () => void;
  isReadOnly?: boolean;
  verificationStatus?: string;
}

export const VerificationStep = ({
  onNext,
  onBack,
  isReadOnly = false,
  verificationStatus,
}: VerificationStepProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [createIdentitySession, { isLoading: isCreating }] =
    useCreateIdentitySessionMutation();
  const [verificationStarted, setVerificationStarted] = useState(false);
  const [modalClosed, setModalClosed] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<Date | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const STUCK_THRESHOLD = 40; // seconds before showing the retry button

  const { data: statusData, refetch } = useGetVerificationStatusQuery(
    undefined,
    {
      pollingInterval: shouldPoll ? 3000 : 0,
    },
  );
  const isVerifying = statusData?.userStatus === "verifying";

  // Show "waiting" screen only after the Stripe modal has closed, or if we're returning to a verifying state
  const isWaitingForVerification = modalClosed || isVerifying || isReadOnly;

  // Tick cooldown countdown every second
  useEffect(() => {
    if (!cooldownUntil) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((cooldownUntil.getTime() - Date.now()) / 1000));
      setCooldownRemaining(remaining);
      if (remaining === 0) setCooldownUntil(null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  // Start polling when verification starts or status is "verifying"
  useEffect(() => {
    if (verificationStarted || isVerifying) {
      setShouldPoll(true);
    }
    // Stop polling once verified
    if (statusData?.isVerified) {
      setShouldPoll(false);
    }
  }, [verificationStarted, isVerifying, statusData?.isVerified]);

  // Elapsed time counter for the waiting state
  useEffect(() => {
    if (isWaitingForVerification && !statusData?.isVerified) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isWaitingForVerification, statusData?.isVerified]);

  // Only auto-advance when verification completes during THIS session
  // (not when the user navigates back from step 5 and is already verified)
  const wasVerifiedOnMount = useRef(statusData?.isVerified ?? false);
  useEffect(() => {
    if (statusData?.isVerified && !wasVerifiedOnMount.current) {
      onNext();
    }
  }, [statusData?.isVerified, onNext]);

  const handleRetry = async () => {
    setIsRetrying(true);
    setVerificationStarted(false);
    setModalClosed(false);
    setElapsedSeconds(0);
    await handleStartVerification();
    setIsRetrying(false);
  };

  const handleStartVerification = async () => {
    setError(null);
    try {
      const stripe = await stripePromise;
      if (!stripe) {
        setError(
          "Failed to load Stripe. Check that REACT_APP_STRIPE_PUBLISHABLE_KEY is set.",
        );
        return;
      }

      const result = await createIdentitySession().unwrap();

      if (!result.client_secret) {
        setError("No client secret returned from server.");
        return;
      }

      setVerificationStarted(true);
      // verifyIdentity() opens the Stripe modal — it resolves when the user closes it
      const verifyResult = await stripe.verifyIdentity(result.client_secret);

      if (verifyResult.error) {
        console.error("Verification error:", verifyResult.error);
        setError(
          verifyResult.error.message ||
            "Verification failed. Please try again.",
        );
        setVerificationStarted(false);
        setModalClosed(false);
      } else {
        // Modal closed successfully — now show the processing screen
        setModalClosed(true);
        setElapsedSeconds(0);
      }
      // Refresh the verification status
      refetch();
    } catch (err: any) {
      console.error("Error creating identity session:", err);
      if (err?.status === 429 && err?.data?.retryAfter) {
        setCooldownUntil(new Date(err.data.retryAfter));
      } else {
        setError("Failed to start identity verification. Please try again.");
      }
    }
  };

  const features = [
    {
      icon: <BadgeIcon />,
      title: "Government ID",
      description: "Take a photo of your driver's license or passport",
    },
    {
      icon: <CameraIcon />,
      title: "Selfie",
      description: "Take a quick selfie to match with your ID",
    },
    {
      icon: <SecurityIcon />,
      title: "Secure & Private",
      description: "Your data is encrypted and handled by Stripe",
    },
  ];

  const isVerificationFailed = verificationStatus === "verification_failed";

  const formatCooldown = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  // Helper to format elapsed time
  const formatElapsed = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Get progressive waiting message based on elapsed time
  const getWaitingMessage = () => {
    if (elapsedSeconds < 15) {
      return "We're verifying your identity. This usually takes 1-2 minutes.";
    }
    if (elapsedSeconds < 40) {
      return "Still processing your documents. Hang tight!";
    }
    if (elapsedSeconds < 60) {
      return "Taking a bit longer than usual. You can wait here or check back later.";
    }
    return "This is taking longer than expected. You can continue to your dashboard and we'll notify you when it's done.";
  };

  // ==========================================
  // STATE: Verified (already done — navigated back here or returning)
  // ==========================================
  if (statusData?.isVerified) {
    return (
      <Stack spacing={3}>
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.success.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <VerifiedUserIcon
              sx={{ fontSize: 40, color: theme.palette.success.main }}
            />
          </Box>
          <Typography
            variant="h6"
            fontWeight={600}
            gutterBottom
            color="success.main"
          >
            Identity Verified
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your identity has been successfully verified.
          </Typography>
        </Box>

        <Alert severity="success" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            Verification Complete
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You can continue to the next step.
          </Typography>
        </Alert>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              flex: 1,
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={onNext}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              flex: 2,
              background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
            }}
          >
            Continue
          </Button>
        </Box>
      </Stack>
    );
  }

  // ==========================================
  // STATE: Cooldown — too soon to retry
  // ==========================================
  if (cooldownRemaining > 0) {
    return (
      <Stack spacing={3}>
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <HourglassTopIcon sx={{ fontSize: 40, color: theme.palette.warning.main }} />
          </Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Please Wait Before Retrying
          </Typography>
          <Typography variant="body2" color="text.secondary">
            To protect your account, we require a waiting period between verification attempts.
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.06),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            You can retry in
          </Typography>
          <Typography variant="h4" fontWeight={700} color="warning.main">
            {formatCooldown(cooldownRemaining)}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="body2">
            Use this time to prepare: ensure your ID is not expired, take photos in good lighting, and make sure all text is clearly visible.
          </Typography>
        </Alert>

        <Button
          variant="outlined"
          size="large"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          sx={{ py: 1.5, fontWeight: 600 }}
        >
          Back
        </Button>
      </Stack>
    );
  }

  // ==========================================
  // STATE: Verification Failed — retry
  // ==========================================
  if (isVerificationFailed) {
    return (
      <Stack spacing={3}>
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.error.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <ErrorOutlineIcon
              sx={{ fontSize: 40, color: theme.palette.error.main }}
            />
          </Box>
          <Typography
            variant="h6"
            fontWeight={600}
            gutterBottom
            color="error.main"
          >
            Verification Failed
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We couldn't verify your identity. This can happen if the photos were
            unclear or the information didn't match.
          </Typography>
        </Box>

        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            What you can do:
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
              <li>Make sure your ID is valid and not expired</li>
              <li>Take photos in good lighting</li>
              <li>Ensure all text on your ID is clearly visible</li>
              <li>Make sure your selfie matches your ID photo</li>
            </ul>
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Features */}
        <Stack spacing={2}>
          {features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.grey[100], 0.5),
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.palette.primary.main,
                }}
              >
                {feature.icon}
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {feature.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {feature.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={onBack}
            disabled={isCreating || verificationStarted}
            startIcon={<ArrowBackIcon />}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              flex: 1,
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleStartVerification}
            disabled={isCreating || verificationStarted}
            endIcon={
              isCreating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <VerifiedUserIcon />
              )
            }
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              flex: 2,
              background: verificationStarted
                ? theme.palette.grey[400]
                : `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            }}
          >
            {isCreating
              ? "Starting..."
              : verificationStarted
                ? "Processing..."
                : "Try Again"}
          </Button>
        </Box>
      </Stack>
    );
  }

  // ==========================================
  // STATE: Waiting for verification result
  // ==========================================
  if (isWaitingForVerification && !statusData?.isVerified) {
    return (
      <Stack spacing={3}>
        {/* Pulsing waiting icon */}
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
              animation: `${pulse} 2s ease-in-out infinite`,
            }}
          >
            <HourglassTopIcon
              sx={{ fontSize: 40, color: theme.palette.warning.main }}
            />
          </Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Verifying Your Identity
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 400, mx: "auto" }}
          >
            {getWaitingMessage()}
          </Typography>
        </Box>

        {/* Progress indicator */}
        <Box
          sx={{
            p: 2.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.05),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress
                size={16}
                thickness={5}
                sx={{ color: theme.palette.warning.main }}
              />
              <Typography variant="body2" fontWeight={600}>
                Processing your documents
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {formatElapsed(elapsedSeconds)}
            </Typography>
          </Box>
          <LinearProgress
            variant="indeterminate"
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                bgcolor: theme.palette.warning.main,
              },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            {elapsedSeconds < STUCK_THRESHOLD
              ? "Typically takes 1-2 minutes"
              : "Try submitting again using the button below"}
          </Typography>
        </Box>

        {/* Learn while you wait */}
        <Box
          component={RouterLink}
          to="/how-it-works"
          target="_blank"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.info.main, 0.08),
            border: `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
            textAlign: "left",
            textDecoration: "none",
            color: "inherit",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: alpha(theme.palette.info.main, 0.12),
              borderColor: theme.palette.info.main,
              transform: "translateY(-2px)",
            },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.15),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.palette.info.main,
              flexShrink: 0,
            }}
          >
            <SchoolIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Learn How Circles Work
            </Typography>
            <Typography variant="caption" color="text.secondary">
              While you wait, learn how lending circles work
            </Typography>
          </Box>
          <OpenInNewIcon
            sx={{ color: theme.palette.info.main, fontSize: 20 }}
          />
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              flex: 1,
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/view-group")}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              flex: 2,
            }}
          >
            Go to Dashboard
          </Button>
        </Box>

        {/* Retry option — prominent after threshold, subtle if only this session */}
        {(elapsedSeconds >= STUCK_THRESHOLD || modalClosed) && (
          <Box sx={{ textAlign: "center" }}>
            {elapsedSeconds >= STUCK_THRESHOLD ? (
              <Button
                variant="outlined"
                size="medium"
                onClick={handleRetry}
                disabled={isCreating || isRetrying}
                startIcon={
                  isRetrying ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <RefreshIcon />
                  )
                }
                sx={{ borderRadius: 2 }}
              >
                {isRetrying ? "Starting..." : "Try submitting again"}
              </Button>
            ) : (
              <Button
                variant="text"
                size="small"
                onClick={handleRetry}
                disabled={isCreating || isRetrying}
                sx={{ color: "text.secondary", fontSize: "0.8rem" }}
              >
                Having trouble? Start over
              </Button>
            )}
          </Box>
        )}
      </Stack>
    );
  }

  // ==========================================
  // STATE: Initial — ready to start verification
  // ==========================================
  return (
    <Stack spacing={3}>
      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <VerifiedUserIcon
            sx={{
              fontSize: 40,
              color: theme.palette.primary.main,
            }}
          />
        </Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Verify Your Identity
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This quick verification helps keep our community safe and secure. It
          only takes about 2 minutes.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Features */}
      <Stack spacing={2}>
        {features.map((feature, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.grey[100], 0.5),
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.palette.primary.main,
              }}
            >
              {feature.icon}
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {feature.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {feature.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>

      {/* Action buttons */}
      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={onBack}
          disabled={isCreating}
          startIcon={<ArrowBackIcon />}
          sx={{
            py: 1.5,
            fontSize: "1rem",
            fontWeight: 600,
            flex: 1,
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleStartVerification}
          disabled={isCreating}
          endIcon={
            isCreating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <VerifiedUserIcon />
            )
          }
          sx={{
            py: 1.5,
            fontSize: "1rem",
            fontWeight: 600,
            flex: 2,
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          }}
        >
          {isCreating ? "Starting..." : "Start Verification"}
        </Button>
      </Box>

      {/* Skip option */}
      <Box sx={{ textAlign: "center" }}>
        <Button
          variant="text"
          size="small"
          onClick={onNext}
          disabled={isCreating}
          sx={{ color: "text.secondary", fontSize: "0.8rem" }}
        >
          Skip for now — verify later
        </Button>
      </Box>
    </Stack>
  );
};
